import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { openQualiopiReport } from "../lib/qualiopiReport";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import {
    GraduationCap, ArrowRight, Plus, Filter, RefreshCw, Users,
    CheckSquare, Square, ChevronRight, X, AlertTriangle, FileCheck2, CalendarClock,
} from "lucide-react";

const FILIERE_LABELS = {
    electrotechnique: "BTS Électrotechnique",
    ciel: "BTS CIEL",
};

const CLOSING_STATUSES = [
    { value: "graduated", label: "Diplômé" },
    { value: "non_graduated", label: "Non diplômé" },
    { value: "reoriented", label: "Réorientation" },
    { value: "abandoned", label: "Abandon" },
    { value: "other", label: "Autre" },
];

function currentSchoolYear() {
    const d = new Date();
    return d.getMonth() >= 8 ? `${d.getFullYear()}-${d.getFullYear() + 1}` : `${d.getFullYear() - 1}-${d.getFullYear()}`;
}

export default function Promotions() {
    const { user } = useAuth();
    const isResponsable = user?.role === "responsable" || user?.role === "admin";

    const [promotions, setPromotions] = useState([]);
    const [filiereFilter, setFiliereFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [detail, setDetail] = useState(null); // { promo, action: "promote" | "close" | "view" }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/promotions");
            setPromotions(Array.isArray(data) ? data : []);
        } catch (e) {
            console.warn("[Promotions] load failed", e);
            toast.error("Impossible de charger les promotions");
            setPromotions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        return promotions.filter((p) => {
            if (filiereFilter !== "all" && p.filiere !== filiereFilter) return false;
            if (statusFilter !== "all" && p.status !== statusFilter) return false;
            return true;
        });
    }, [promotions, filiereFilter, statusFilter]);

    return (
        <div className="min-h-screen flex flex-col bg-cream">
            <Navbar />
            <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-ink flex items-center gap-2" data-testid="promotions-title">
                            <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 text-primary" /> Promotions
                        </h1>
                        <p className="text-sm text-ink/70 mt-1">
                            Une promotion regroupe les étudiants qui suivent un même cursus sur deux années. Une fois créée, l'étudiant conserve son profil unique pendant toute sa scolarité.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Link to="/etudiants" className="btn-outline text-sm inline-flex items-center gap-1.5" data-testid="promotions-goto-students">
                            <Users className="w-4 h-4" /> Étudiants
                        </Link>
                        {isResponsable && (
                            <button
                                onClick={() => setShowCreate(true)}
                                className="btn-primary text-sm inline-flex items-center gap-1.5"
                                data-testid="promotions-new-btn"
                            >
                                <Plus className="w-4 h-4" /> Nouvelle promotion
                            </button>
                        )}
                    </div>
                </div>

                {/* Filtres */}
                <section className="rounded-2xl bg-white border border-ink/10 p-4 sm:p-5 shadow-sm">
                    <div className="flex items-center gap-2 mb-3 text-ink/70 text-sm font-medium">
                        <Filter className="w-4 h-4" /> Filtres
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-ink/70 mb-1">Formation</label>
                            <select
                                value={filiereFilter}
                                onChange={(e) => setFiliereFilter(e.target.value)}
                                className="input text-sm w-full"
                                data-testid="promotions-filter-filiere"
                            >
                                <option value="all">Toutes</option>
                                {Object.entries(FILIERE_LABELS).map(([v, l]) => (
                                    <option key={v} value={v}>{l}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-ink/70 mb-1">Statut</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="input text-sm w-full"
                                data-testid="promotions-filter-status"
                            >
                                <option value="all">Tous</option>
                                <option value="active">Active</option>
                                <option value="closed">Clôturée</option>
                                <option value="archived">Archivée</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <button onClick={load} className="btn-outline text-xs inline-flex items-center gap-1.5" data-testid="promotions-refresh">
                                <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
                            </button>
                        </div>
                    </div>
                </section>

                {/* Liste */}
                {loading ? (
                    <div className="p-8 text-center text-ink/60">Chargement…</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center rounded-2xl bg-white border border-ink/10">
                        <GraduationCap className="w-10 h-10 mx-auto text-ink/30 mb-3" />
                        <p className="text-ink/70">Aucune promotion pour l'instant.</p>
                        {isResponsable && (
                            <button onClick={() => setShowCreate(true)} className="btn-primary text-sm mt-3 inline-flex items-center gap-1.5">
                                <Plus className="w-4 h-4" /> Créer une promotion
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="promotions-grid">
                        {filtered.map((p) => (
                            <PromotionCard
                                key={p.id}
                                promo={p}
                                isResponsable={isResponsable}
                                onOpenPromote={() => setDetail({ promo: p, action: "promote" })}
                                onOpenClose={() => setDetail({ promo: p, action: "close" })}
                                onView={() => setDetail({ promo: p, action: "view" })}
                                onChanged={load}
                            />
                        ))}
                    </div>
                )}
            </main>

            {showCreate && (
                <CreatePromotionModal
                    onClose={() => setShowCreate(false)}
                    onCreated={() => { setShowCreate(false); load(); }}
                />
            )}
            {detail && (
                <PromotionActionsModal
                    context={detail}
                    onClose={() => setDetail(null)}
                    onDone={() => { setDetail(null); load(); }}
                />
            )}

            <Footer />
        </div>
    );
}

function PromotionEndDate({ promo, isResponsable, onChanged }) {
    const save = async (value) => {
        if (!value || value === promo.end_date) return;
        try {
            await api.put(`/promotions/${promo.id}`, { end_date: value });
            toast.success("Date de fin mise à jour — la purge RGPD est calculée 12 mois après.");
            onChanged();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };
    return (
        <div className="flex items-center gap-2 text-xs text-ink/70" data-testid={`promo-end-date-${promo.id}`}>
            <CalendarClock className="w-3.5 h-3.5 text-orange" />
            <span>Fin de formation :</span>
            {isResponsable ? (
                <input
                    type="date"
                    defaultValue={promo.end_date || ""}
                    onBlur={(e) => save(e.target.value)}
                    className="border border-ink/15 rounded px-1.5 py-0.5 text-xs"
                    data-testid={`promo-end-date-input-${promo.id}`}
                />
            ) : (
                <span className="font-mono">{promo.end_date ? new Date(promo.end_date).toLocaleDateString("fr-FR") : "—"}</span>
            )}
        </div>
    );
}

function QualiopiButton({ promo }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.get(`/promotions/${promo.id}/qualiopi-report`);
            openQualiopiReport(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };
    return (
        <button onClick={run} disabled={busy} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`promo-qualiopi-${promo.id}`}>
            <FileCheck2 className="w-3.5 h-3.5" /> {busy ? "Génération…" : "Dossier Qualiopi"}
        </button>
    );
}

function PromotionCard({ promo, isResponsable, onOpenPromote, onOpenClose, onView, onChanged }) {
    return (
        <div className="rounded-2xl bg-white border border-ink/10 p-5 shadow-sm space-y-3" data-testid={`promo-card-${promo.id}`}>
            <div>
                <div className="text-xs uppercase tracking-wide text-ink/50">
                    {FILIERE_LABELS[promo.filiere] || promo.filiere}
                </div>
                <div className="text-lg font-bold text-ink flex items-center gap-2">
                    {promo.name}
                    {promo.status !== "active" && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-ink/10 text-ink/60">{promo.status}</span>
                    )}
                </div>
                <div className="text-xs text-ink/60">
                    {promo.year_start} → {promo.year_end}
                </div>
            </div>
            <PromotionEndDate promo={promo} isResponsable={isResponsable} onChanged={onChanged} />
            <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg bg-cream/60 border border-ink/5 px-2 py-1.5">
                    <div className="text-ink/50">1ère</div>
                    <div className="text-base font-semibold text-ink">{promo.counts?.["1ere"] || 0}</div>
                </div>
                <div className="rounded-lg bg-cream/60 border border-ink/5 px-2 py-1.5">
                    <div className="text-ink/50">2ème</div>
                    <div className="text-base font-semibold text-ink">{promo.counts?.["2eme"] || 0}</div>
                </div>
                <div className="rounded-lg bg-cream/60 border border-ink/5 px-2 py-1.5">
                    <div className="text-ink/50">Diplômés</div>
                    <div className="text-base font-semibold text-ink">{promo.counts?.graduated || 0}</div>
                </div>
                <div className="rounded-lg bg-cream/60 border border-ink/5 px-2 py-1.5">
                    <div className="text-ink/50">Total</div>
                    <div className="text-base font-semibold text-ink">{promo.counts?.total || 0}</div>
                </div>
            </div>
            <div className="pt-2 flex flex-wrap gap-2">
                <button onClick={onView} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`promo-view-${promo.id}`}>
                    Voir <ChevronRight className="w-3.5 h-3.5" />
                </button>
                <QualiopiButton promo={promo} />
                {isResponsable && promo.status === "active" && (promo.counts?.["1ere"] || 0) > 0 && (
                    <button onClick={onOpenPromote} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`promo-promote-${promo.id}`}>
                        <ArrowRight className="w-3.5 h-3.5" /> Passer en 2ème
                    </button>
                )}
                {isResponsable && promo.status === "active" && (promo.counts?.["2eme"] || 0) > 0 && (
                    <button onClick={onOpenClose} className="btn-outline text-xs inline-flex items-center gap-1" data-testid={`promo-close-${promo.id}`}>
                        <GraduationCap className="w-3.5 h-3.5" /> Clôturer
                    </button>
                )}
            </div>
        </div>
    );
}

function CreatePromotionModal({ onClose, onCreated }) {
    const [filiere, setFiliere] = useState("electrotechnique");
    const cy = currentSchoolYear();
    const [yearStart, setYearStart] = useState(parseInt(cy.split("-")[0], 10));
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (!filiere || !yearStart) return;
        setBusy(true);
        try {
            await api.post("/promotions", { filiere, year_start: Number(yearStart) });
            toast.success("Promotion créée");
            onCreated();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Création impossible");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" data-testid="promo-create-modal">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-ink">Nouvelle promotion</h2>
                    <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-ink/70">Formation</label>
                        <select value={filiere} onChange={(e) => setFiliere(e.target.value)} className="input text-sm w-full" data-testid="promo-create-filiere">
                            {Object.entries(FILIERE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-ink/70">Année d'entrée (1ère année)</label>
                        <input
                            type="number" min={2020} max={2099}
                            value={yearStart}
                            onChange={(e) => setYearStart(e.target.value)}
                            className="input text-sm w-full"
                            data-testid="promo-create-year"
                        />
                        <div className="text-[10px] text-ink/50 mt-1">La promotion s'étale sur 2 ans : {yearStart} → {Number(yearStart) + 2}</div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
                    <button onClick={submit} disabled={busy} className="btn-primary text-sm" data-testid="promo-create-submit">
                        {busy ? "…" : "Créer"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PromotionActionsModal({ context, onClose, onDone }) {
    const { promo, action } = context;
    const [detail, setDetail] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [statuses, setStatuses] = useState({}); // student_id -> status
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/promotions/${promo.id}`);
                setDetail(data);
                // Pré-sélectionne tous les étudiants concernés
                const eligible = (data.students || []).filter((s) => {
                    if (action === "promote") return s.enrollment?.status === "active" && s.enrollment?.niveau === "1ere";
                    if (action === "close") return s.enrollment?.status === "active" && s.enrollment?.niveau === "2eme";
                    return true;
                });
                setSelected(new Set(eligible.map((s) => s.student_id)));
                const st = {};
                eligible.forEach((s) => { st[s.student_id] = action === "close" ? "graduated" : ""; });
                setStatuses(st);
            } catch (e) {
                console.warn("[Promotions] load detail failed", e);
                toast.error("Impossible de charger les étudiants");
            } finally {
                setLoading(false);
            }
        })();
    }, [promo.id, action]);

    const eligible = useMemo(() => {
        if (!detail) return [];
        return (detail.students || []).filter((s) => {
            if (action === "promote") return s.enrollment?.niveau === "1ere" && s.enrollment?.status === "active";
            if (action === "close") return s.enrollment?.niveau === "2eme" && s.enrollment?.status === "active";
            return true;
        });
    }, [detail, action]);

    const toggle = (sid) => {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(sid)) n.delete(sid); else n.add(sid);
            return n;
        });
    };

    const submit = async () => {
        if (!selected.size) { toast.error("Sélectionnez au moins un étudiant"); return; }
        setBusy(true);
        try {
            if (action === "promote") {
                const { data } = await api.post(`/promotions/${promo.id}/promote`, { student_ids: Array.from(selected) });
                toast.success(`${data.promoted} étudiant(s) passé(s) en 2ème année`);
                if (data.errors?.length) console.warn(data.errors);
            } else if (action === "close") {
                const items = Array.from(selected).map((sid) => ({ student_id: sid, status: statuses[sid] || "graduated" }));
                const { data } = await api.post(`/promotions/${promo.id}/close`, { items });
                toast.success(`${data.closed} étudiant(s) clôturé(s)`);
            }
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Action impossible");
        } finally {
            setBusy(false);
        }
    };

    const isView = action === "view";
    const displayList = isView ? (detail?.students || []) : eligible;

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col shadow-xl" data-testid="promo-action-modal">
                <div className="flex items-center justify-between p-5 border-b border-ink/10">
                    <div>
                        <h2 className="text-lg font-bold text-ink">{promo.name}</h2>
                        <p className="text-xs text-ink/60 mt-0.5">
                            {action === "promote" && "Sélectionnez les étudiants à faire passer en 2ème année"}
                            {action === "close" && "Sélectionnez les étudiants et leur statut final"}
                            {action === "view" && `${detail?.students?.length || 0} étudiant(s)`}
                        </p>
                    </div>
                    <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 overflow-auto p-5">
                    {loading ? (
                        <div className="text-center text-ink/60 py-8">Chargement…</div>
                    ) : displayList.length === 0 ? (
                        <div className="text-center py-8 space-y-2">
                            <AlertTriangle className="w-8 h-8 mx-auto text-amber-500" />
                            <p className="text-sm text-ink/70">
                                Aucun étudiant éligible pour cette action.
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {displayList.map((s) => (
                                <div
                                    key={s.student_id}
                                    className={`flex items-center gap-3 p-3 rounded-xl border ${selected.has(s.student_id) ? "border-primary/50 bg-primary/5" : "border-ink/10"}`}
                                >
                                    {!isView && (
                                        <button onClick={() => toggle(s.student_id)} aria-label="Sélectionner" data-testid={`action-select-${s.student_id}`}>
                                            {selected.has(s.student_id) ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5 text-ink/40" />}
                                        </button>
                                    )}
                                    <div className="flex-1">
                                        <div className="font-medium text-ink">{(s.prenom || "") + " " + (s.nom || "")}</div>
                                        <div className="text-xs text-ink/60">{s.email} · {s.enrollment?.niveau === "1ere" ? "1ère année" : s.enrollment?.niveau === "2eme" ? "2ème année" : "—"}</div>
                                    </div>
                                    {action === "close" && selected.has(s.student_id) && (
                                        <select
                                            value={statuses[s.student_id] || "graduated"}
                                            onChange={(e) => setStatuses((prev) => ({ ...prev, [s.student_id]: e.target.value }))}
                                            className="input text-xs"
                                            data-testid={`action-status-${s.student_id}`}
                                        >
                                            {CLOSING_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                        </select>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {!isView && (
                    <div className="flex justify-between items-center p-5 border-t border-ink/10">
                        <div className="text-xs text-ink/60">{selected.size} sélectionné(s)</div>
                        <div className="flex gap-2">
                            <button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
                            <button onClick={submit} disabled={busy || !selected.size} className="btn-primary text-sm" data-testid="promo-action-submit">
                                {busy ? "…" : action === "promote" ? "Faire passer en 2ème année" : "Clôturer la promotion"}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
